import { useState, useCallback, useMemo, useEffect, useId } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { DownIcon } from '@common/components/icons';
import { useClickOutside } from '@common/hooks/useClickOutside';
import * as styles from './EditableStatusDropdown.module.css';

/**
 * Status type presets for common use cases
 */
export const STATUS_TYPES = {
  ENABLED_DISABLED: 'enabled_disabled',
  PUBLISHED_DRAFT: 'published_draft',
};

// Custom event name for closing all dropdowns
const CLOSE_ALL_DROPDOWNS_EVENT = 'editableStatusDropdown:closeAll';

/**
 * EditableStatusDropdown Component
 * Reusable dropdown for editing status in tables with inline editing.
 * Supports different status types (Enabled/Disabled, Published/Draft) and optional status dot indicator.
 *
 * @param {Object} props
 * @param {number} props.value - Current value (0 or 1)
 * @param {function} props.onUpdate - Callback when value changes
 * @param {boolean} props.disabled - Whether the dropdown is disabled
 * @param {boolean} props.showStatusDot - Whether to show the status dot indicator
 * @param {string} props.statusType - Type of status labels to use ('enabled_disabled' or 'published_draft')
 */
function EditableStatusDropdown({
  value,
  onUpdate,
  disabled = false,
  showStatusDot = false,
  statusType = STATUS_TYPES.ENABLED_DISABLED,
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownId = useId();

  // Get labels based on status type
  const labels = useMemo(() => {
    if (statusType === STATUS_TYPES.PUBLISHED_DRAFT) {
      return {
        active: t('reports.status.published', 'Published'),
        inactive: t('reports.status.draft', 'Draft'),
      };
    }
    // Default: Enabled/Disabled
    return {
      active: t('common.enabled', 'Enabled'),
      inactive: t('common.disabled', 'Disabled'),
    };
  }, [statusType, t]);

  // Close dropdown when clicking outside - hook returns the ref to attach
  const closeDropdown = useCallback(() => setIsOpen(false), []);
  const containerRef = useClickOutside(closeDropdown, isOpen);

  // Listen for close all dropdowns event (when another dropdown opens)
  useEffect(() => {
    const handleCloseAll = (event) => {
      // Close this dropdown if a different dropdown triggered the event
      if (event.detail?.sourceId !== dropdownId) {
        setIsOpen(false);
      }
    };

    document.addEventListener(CLOSE_ALL_DROPDOWNS_EVENT, handleCloseAll);
    return () => {
      document.removeEventListener(CLOSE_ALL_DROPDOWNS_EVENT, handleCloseAll);
    };
  }, [dropdownId]);

  const handleToggle = (e) => {
    e.stopPropagation(); // Prevent row click
    if (!disabled) {
      const newIsOpen = !isOpen;
      // If opening, dispatch event to close all other dropdowns
      if (newIsOpen) {
        document.dispatchEvent(
          new CustomEvent(CLOSE_ALL_DROPDOWNS_EVENT, {
            detail: { sourceId: dropdownId },
          }),
        );
      }
      setIsOpen(newIsOpen);
    }
  };

  const handleSelect = (e, newValue) => {
    e.stopPropagation(); // Prevent row click
    if (onUpdate && newValue !== value) {
      onUpdate(newValue);
    }
    setIsOpen(false);
  };

  // Stop propagation on container click to prevent row click when clicking anywhere in dropdown
  const handleContainerClick = (e) => {
    e.stopPropagation();
  };

  // Handle keyboard events for accessibility
  const handleContainerKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      setIsOpen(false);
    }
  };

  const isActive = value === 1;
  const displayValue = isActive ? labels.active : labels.inactive;
  const statusDotClass = isActive ? styles.statusDotEnabled : styles.statusDotDisabled;

  return (
    <div
      ref={containerRef}
      className={styles.container}
      onClick={handleContainerClick}
      onKeyDown={handleContainerKeyDown}
      role="presentation"
    >
      <button
        type="button"
        className={`${styles.trigger} ${isOpen ? styles.triggerOpen : ''}`}
        disabled={disabled}
        onClick={handleToggle}
      >
        <span className={styles.statusText}>
          {showStatusDot && <span className={`${styles.statusDot} ${statusDotClass}`} />}
          {displayValue}
        </span>
        <DownIcon className={`${styles.arrow} ${isOpen ? styles.arrowOpen : ''}`} />
      </button>
      {isOpen && !disabled && (
        <div className={styles.menu}>
          <button
            type="button"
            className={`${styles.option} ${value === 1 ? styles.optionSelected : ''}`}
            onClick={(e) => handleSelect(e, 1)}
          >
            <span className={styles.statusText}>
              {showStatusDot && <span className={`${styles.statusDot} ${styles.statusDotEnabled}`} />}
              {labels.active}
            </span>
          </button>
          <button
            type="button"
            className={`${styles.option} ${value === 0 ? styles.optionSelected : ''}`}
            onClick={(e) => handleSelect(e, 0)}
          >
            <span className={styles.statusText}>
              {showStatusDot && <span className={`${styles.statusDot} ${styles.statusDotDisabled}`} />}
              {labels.inactive}
            </span>
          </button>
        </div>
      )}
    </div>
  );
}

EditableStatusDropdown.propTypes = {
  value: PropTypes.oneOf([0, 1]).isRequired,
  onUpdate: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  showStatusDot: PropTypes.bool,
  statusType: PropTypes.oneOf([STATUS_TYPES.ENABLED_DISABLED, STATUS_TYPES.PUBLISHED_DRAFT]),
};

EditableStatusDropdown.defaultProps = {
  disabled: false,
  showStatusDot: false,
  statusType: STATUS_TYPES.ENABLED_DISABLED,
};

export default EditableStatusDropdown;

